import { serverUrl } from "../services/serverOrigin.js";
import { cn } from "../lib/utils.js";

/**
 * Somebody's face, or a stand-in that is never a blank circle.
 *
 * # Everybody has one from the moment they exist
 *
 * A guest joins a party with a name and nothing else (ADR 0007), and the people
 * most likely to be strangers in the room are exactly the ones who will never
 * upload a picture. So the fallback is not a grey silhouette — it is generated
 * from the identity itself: an initial on a hue derived from the profile id.
 *
 * That makes it **stable and distinct**. The same person is the same colour on
 * the phone, on the television and in the recap, and two players in a lobby are
 * almost never the same colour — which is the actual job of an avatar in a game
 * where six people are looking at one screen trying to find their own row.
 *
 * # The hue comes from the id, not the name
 *
 * Two guests called "Ben" must not be the same colour, and one person renaming
 * themselves mid-party must not change colour — a face that moves is worse than
 * no face. `profiles.id` is the thing that is stable and unique, so it is what
 * the hue is derived from.
 */
export function Avatar({
  name,
  profileId,
  avatar = null,
  size = 36,
  className,
}: {
  /** Display name — the initial, and the accessible label. */
  name: string;
  /** Profile id. The hue is derived from this, so it is stable per person. */
  profileId: string;
  /** Stored avatar digest, or null for the generated fallback. */
  avatar?: string | null;
  /** Edge length in pixels. */
  size?: number;
  className?: string;
}) {
  const shared = cn("shrink-0 rounded-full object-cover", className);

  if (avatar) {
    return (
      <img
        // Content-addressed, so this URL can never point at different bytes —
        // which is why the server marks it immutable and why nothing here needs
        // a cache-busting parameter.
        src={serverUrl(`/api/avatar/${avatar}`)}
        width={size}
        height={size}
        style={{ width: size, height: size }}
        // Empty: the name is already beside this everywhere it appears, and a
        // screen reader announcing "Ben, Ben" is noise. Where it stands alone
        // the caller supplies a label.
        alt=""
        className={cn(shared, "bg-stage-800")}
      />
    );
  }

  const hue = hueFor(profileId);
  return (
    <span
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        fontSize: Math.round(size * 0.42),
        // oklch, like the rest of the palette: an evenly-spaced hue wheel in
        // sRGB is not evenly spaced *perceptually*, so two ids 40° apart can
        // look identical. Fixed lightness and chroma keep every generated
        // avatar equally legible on the dark board.
        backgroundColor: `oklch(0.62 0.15 ${hue})`,
        color: "oklch(0.97 0.02 " + String(hue) + ")",
      }}
      className={cn(
        shared,
        "inline-flex items-center justify-center font-bold select-none",
      )}
    >
      {initial(name)}
    </span>
  );
}

/**
 * The first character of a name, as a person would write it.
 *
 * `Array.from` rather than `name[0]`: a name beginning with an emoji or any
 * astral-plane character would otherwise be split mid-surrogate and render as a
 * replacement box — and people absolutely put emoji in party names.
 */
function initial(name: string): string {
  const first = Array.from(name.trim())[0];
  return (first ?? "?").toUpperCase();
}

/**
 * A stable hue in [0, 360) from a profile id.
 *
 * FNV-1a: tiny, well-distributed for short strings, and deterministic across
 * every device that renders this person — which is the whole requirement. It is
 * not a security hash and nothing here depends on it being one.
 */
function hueFor(id: string): number {
  let hash = 0x811c9dc5;
  for (let i = 0; i < id.length; i += 1) {
    hash ^= id.charCodeAt(i);
    // `Math.imul` for a real 32-bit multiply; `*` would silently go through a
    // double and lose the low bits that make this distribute at all.
    hash = Math.imul(hash, 0x01000193);
  }
  return Math.abs(hash) % 360;
}
